import { api } from '../../../core/utils/apiClient';
import {
  LinkedInConsentSubmission,
  LinkedInEnrichmentData,
  LinkedInEnrichmentStatus,
  LinkedInEnrichmentTriggerResponse,
  LinkedInKillSwitch,
  LinkedInRetryResponse,
  LinkedInUsageEntry,
} from '../types';

class LinkedInEnrichmentService {
  async submitConsent(
    companyId: number,
    linkedinUrl: string,
    consentAcknowledged: boolean
  ): Promise<LinkedInConsentSubmission> {
    const response = await api.post(
      `/api/v1/linkedin/consent/${companyId}`,
      {
        linkedin_url: linkedinUrl,
        consent_acknowledged: consentAcknowledged,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail ||
          error?.message ||
          'Failed to submit LinkedIn consent'
      );
    }

    return response.json();
  }

  async triggerEnrichment(
    companyId: number,
    linkedinUrl: string,
    forceRefresh: boolean = false
  ): Promise<LinkedInEnrichmentTriggerResponse> {
    const response = await api.post(
      `/api/v1/linkedin/enrich/${companyId}`,
      {
        linkedin_url: linkedinUrl,
        force_refresh: forceRefresh,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail ||
          error?.message ||
          'Failed to trigger LinkedIn enrichment'
      );
    }

    return response.json();
  }

  async getStatus(companyId: number): Promise<LinkedInEnrichmentStatus> {
    const response = await api.get(
      `/api/v1/linkedin/status/${companyId}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load LinkedIn enrichment status'
      );
    }

    return response.json();
  }

  async getData(companyId: number): Promise<LinkedInEnrichmentData> {
    const response = await api.get(
      `/api/v1/linkedin/data/${companyId}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load LinkedIn data'
      );
    }

    return response.json();
  }

  async retry(companyId: number): Promise<LinkedInRetryResponse> {
    const response = await api.post(
      `/api/v1/linkedin/retry/${companyId}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Unable to retry LinkedIn enrichment'
      );
    }

    return response.json();
  }

  async getUsageStats(days: number = 30): Promise<LinkedInUsageEntry[]> {
    const response = await api.get(
      `/api/v1/linkedin/usage/stats?days=${days}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load LinkedIn usage data'
      );
    }

    return response.json();
  }

  async getKillSwitch(): Promise<LinkedInKillSwitch> {
    const response = await api.get(`/api/v1/linkedin/admin/kill-switch`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load kill switch status'
      );
    }

    return response.json();
  }

  async toggleKillSwitch(enabled: boolean): Promise<LinkedInKillSwitch> {
    const response = await api.post(
      `/api/v1/linkedin/admin/kill-switch`,
      {
        enabled,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to update kill switch'
      );
    }

    return response.json();
  }
}

export const linkedInEnrichmentService = new LinkedInEnrichmentService();

